import {
  Box,
  ButtonGroup,
  Container,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  SkeletonText,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { ColumnDef, flexRender } from "@tanstack/react-table";
import iconArrowLeftRight from "assets/icons/icon-arrow-left-right.svg";
import IconEye from "assets/icons/icon-eye.svg?react";
import ButtonCreateNew from "components/ui/ButtonCreateNew";
import CheckboxSelect from "components/ui/CheckboxSelect/CheckboxSelect";
import CustomRangeDatepicker from "components/ui/Datepicker/CustomRangeDatepicker";
import DateTime from "components/ui/DateTime";
import EmailRowTitle from "components/ui/EmailRowTitle";
import EmptyList from "components/ui/EmptyList";
import FilterGroup from "components/ui/FilterGroup";
import Pagination from "components/ui/Pagination";
import SearchInput from "components/ui/SearchInput";
import ServerDynamicTableProvider, {
  useServerDynamicTableContext,
} from "contexts/server-table/ServerDynamicTableContext";
import useServerTableUrlState from "hooks/server-table/useServerTableUrlState";
import { useMessages } from "hooks/useMessages";
import { useResourceList } from "hooks/useResourceList";
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LinkContentPreview } from "../config";
import { useDuplicateContent } from "../hooks/useDuplicateContent";
import ButtonDuplicateContent from "./ButtonDuplicateContent";

type DuplicateFilterKey =
  | "subcategoryIdIn"
  | "createdByIdIn"
  | "legislativeProposalIdIn"
  | "authorIdIn"
  | "rapporteurIdIn"
  | "createdAtFrom"
  | "createdAtTo";

const duplicateFilterDefaults: Record<DuplicateFilterKey, string> = {
  subcategoryIdIn: "",
  createdByIdIn: "",
  legislativeProposalIdIn: "",
  authorIdIn: "",
  rapporteurIdIn: "",
  createdAtFrom: "",
  createdAtTo: "",
};

export default function ListContents() {
  const { id = "" } = useParams<{ id: string }>();
  const {
    pagination,
    globalFilter,
    filters,
    setPagination,
    setGlobalFilter,
    setFilter,
  } = useServerTableUrlState<DuplicateFilterKey>({
    filterDefaults: duplicateFilterDefaults,
  });

  const selectedDateRange = React.useMemo(() => {
    const dates: Date[] = [];
    const fromDate = parseIsoDate(filters.createdAtFrom);
    const toDate = parseIsoDate(filters.createdAtTo);

    if (fromDate) {
      dates.push(fromDate);
    }
    if (toDate) {
      dates.push(toDate);
    }

    return dates;
  }, [filters.createdAtFrom, filters.createdAtTo]);

  const resourceParams = React.useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      q: globalFilter || undefined,
      subcategoryIdIn: filters.subcategoryIdIn || undefined,
      createdByIdIn: filters.createdByIdIn || undefined,
      legislativeProposalIdIn: filters.legislativeProposalIdIn || undefined,
      authorIdIn: filters.authorIdIn || undefined,
      rapporteurIdIn: filters.rapporteurIdIn || undefined,
      createdAtFrom: filters.createdAtFrom || undefined,
      createdAtTo: filters.createdAtTo || undefined,
    }),
    [
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
      filters.subcategoryIdIn,
      filters.createdByIdIn,
      filters.legislativeProposalIdIn,
      filters.authorIdIn,
      filters.rapporteurIdIn,
      filters.createdAtFrom,
      filters.createdAtTo,
    ],
  );

  const columns = React.useMemo<ColumnDef<Record<string, unknown>>[]>(
    () => [
      {
        header: "Título",
        accessorKey: "title",
        cell: ({ row }) => <EmailRowTitle title={String(row.original.title)} />,
      },
      {
        header: "Poder/Órgão",
        accessorKey: "category",
        accessorFn: (row) =>
          `${String((row.category as Record<string, unknown>).name)} - ${String(
            (row.subcategory as Record<string, unknown>).name,
          )}`,
      },
      {
        header: "PL",
        accessorKey: "legislativeProposal",
        accessorFn: (row) =>
          String((row.legislativeProposal as Record<string, unknown>)?.name ?? ""),
      },
      {
        header: "Criado por",
        accessorKey: "createdBy",
        accessorFn: (row) => String((row.createdBy as Record<string, unknown>).name),
      },
      {
        header: "Criado em",
        accessorKey: "createdAt",
        cell: (props) => <DateTime date={props.cell.getValue()} />,
      },
    ],
    []
  );

  return (
    <ServerDynamicTableProvider
      columns={columns}
      pagination={pagination}
      globalFilter={globalFilter}
      onPaginationChange={setPagination}
      onGlobalFilterChange={setGlobalFilter}
      resource={{
        resourceName: "duplicableContents",
        path: `contents/duplicate/${id}`,
        params: resourceParams,
        dataKey: "contents",
      }}
      tableOptions={{
        enableGlobalFilter: true,
        initialState: {
          columnVisibility: {
            authors: false,
            rapporteurs: false,
          },
        },
      }}
    >
      <Container
        variant="internal"
        flexDirection="column"
        mb="80px"
        minH={{ lg: "700px" }}
      >
        <Flex alignItems="center">
          <Heading as="h1" size="h1">
            Como deseja criar o conteúdo?
            <span>
              Para continuar, selecione um conteúdo a partir da listagem abaixo
              para usá-lo como base ou crie um novo conteúdo em branco
            </span>
          </Heading>

          <ButtonCreateNew
            ml="auto"
            title="Criar novo conteúdo"
            to={`/disparos-de-report/novo/${id}/conteudo`}
          />
        </Flex>

        <Filters
          globalFilter={globalFilter}
          onGlobalFilterChange={setGlobalFilter}
          selectedDateRange={selectedDateRange}
          onDateRangeChange={(dates) => {
            const [startDate, endDate] = dates;
            setFilter("createdAtFrom", startDate ? startOfDayIso(startDate) : "");
            setFilter("createdAtTo", endDate ? endOfDayIso(endDate) : "");
          }}
          selectedSubcategoryIds={parseCsv(filters.subcategoryIdIn)}
          onSubcategoryChange={(ids) => setFilter("subcategoryIdIn", serializeCsv(ids))}
          selectedCreatedByIds={parseCsv(filters.createdByIdIn)}
          onCreatedByChange={(ids) => setFilter("createdByIdIn", serializeCsv(ids))}
          selectedLegislativeProposalIds={parseCsv(filters.legislativeProposalIdIn)}
          onLegislativeProposalChange={(ids) =>
            setFilter("legislativeProposalIdIn", serializeCsv(ids))
          }
          selectedAuthorIds={parseCsv(filters.authorIdIn)}
          onAuthorChange={(ids) => setFilter("authorIdIn", serializeCsv(ids))}
          selectedRapporteurIds={parseCsv(filters.rapporteurIdIn)}
          onRapporteurChange={(ids) => setFilter("rapporteurIdIn", serializeCsv(ids))}
        />
        <List />
      </Container>
    </ServerDynamicTableProvider>
  );
}

interface FiltersProps {
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  selectedDateRange: Date[];
  onDateRangeChange: (dates: Date[]) => void;
  selectedSubcategoryIds: string[];
  onSubcategoryChange: (ids: string[]) => void;
  selectedCreatedByIds: string[];
  onCreatedByChange: (ids: string[]) => void;
  selectedLegislativeProposalIds: string[];
  onLegislativeProposalChange: (ids: string[]) => void;
  selectedAuthorIds: string[];
  onAuthorChange: (ids: string[]) => void;
  selectedRapporteurIds: string[];
  onRapporteurChange: (ids: string[]) => void;
}

function Filters({
  globalFilter,
  onGlobalFilterChange,
  selectedDateRange,
  onDateRangeChange,
  selectedSubcategoryIds,
  onSubcategoryChange,
  selectedCreatedByIds,
  onCreatedByChange,
  selectedLegislativeProposalIds,
  onLegislativeProposalChange,
  selectedAuthorIds,
  onAuthorChange,
  selectedRapporteurIds,
  onRapporteurChange,
}: FiltersProps) {
  const { id } = useParams();

  const { data: authors } = useResourceList({
    resourceName: "authors",
    path: "content/authors",
  });

  const { data: people } = useResourceList({
    resourceName: "people",
  });

  const { data: pls } = useResourceList({
    resourceName: "pls",
  });

  const { data: categories = [] } = useResourceList({
    resourceName: "categories",
    path: "/template-categories",
    params: { contentGroupId: id },
  });

  const subcategoryOptions = categories.reduce((acc, category) => {
    if (category.subcategories) {
      return [
        ...acc,
        ...category.subcategories.map((subcategory) => ({
          value: String(subcategory.id),
          label: `${category.name} - ${subcategory.name}`,
        })),
      ];
    }
    return acc;
  }, [] as { value: string; label: string }[]);

  const createdByOptions = authors?.map((c) => ({ value: String(c.id), label: c.name })) ?? [];
  const legislativeProposalOptions = pls?.map((c) => ({ value: String(c.id), label: c.name })) ?? [];
  const peopleOptions = people?.map((c) => ({ value: String(c.id), label: c.name })) ?? [];

  return (
    <FilterGroup mt="60px" justifyContent="start" zIndex={1} pos="relative">
      <Box flex={1} minW="180px">
        <FormLabel>Buscar</FormLabel>
        <SearchInput
          placeholder="Buscar por título"
          value={globalFilter}
          onChange={(value) => onGlobalFilterChange(String(value))}
        />
      </Box>

      <Box>
        <FormLabel>Filtrar por Poder/Órgão</FormLabel>
        <CheckboxSelect
          placeholder="Todas os órgãos"
          options={subcategoryOptions}
          value={subcategoryOptions.filter((option) =>
            selectedSubcategoryIds.includes(option.value),
          )}
          onChange={(value) => onSubcategoryChange(value.map((v) => String(v.value)))}
        />
      </Box>

      <Box>
        <FormLabel>Filtrar por criador</FormLabel>
        <CheckboxSelect
          placeholder="Todos os criadores"
          options={createdByOptions}
          value={createdByOptions.filter((option) =>
            selectedCreatedByIds.includes(option.value),
          )}
          onChange={(value) => onCreatedByChange(value.map((v) => String(v.value)))}
        />
      </Box>

      <Box>
        <FormLabel>Filtrar por período</FormLabel>
        <CustomRangeDatepicker dates={selectedDateRange} onDateChange={onDateRangeChange} />
      </Box>

      <Box>
        <FormLabel>Filtrar por PLs</FormLabel>
        <CheckboxSelect
          placeholder="Indiferente"
          options={legislativeProposalOptions}
          value={legislativeProposalOptions.filter((option) =>
            selectedLegislativeProposalIds.includes(option.value),
          )}
          onChange={(value) =>
            onLegislativeProposalChange(value.map((v) => String(v.value)))
          }
        />
      </Box>

      <Box>
        <FormLabel>Autor da PL</FormLabel>
        <CheckboxSelect
          placeholder="Indiferente"
          options={peopleOptions}
          value={peopleOptions.filter((option) => selectedAuthorIds.includes(option.value))}
          onChange={(value) => onAuthorChange(value.map((v) => String(v.value)))}
        />
      </Box>

      <Box>
        <FormLabel>Relator da PL</FormLabel>
        <CheckboxSelect
          placeholder="Indiferente"
          options={peopleOptions}
          value={peopleOptions.filter((option) =>
            selectedRapporteurIds.includes(option.value),
          )}
          onChange={(value) => onRapporteurChange(value.map((v) => String(v.value)))}
        />
      </Box>
    </FilterGroup>
  );
}

function List() {
  const {
    table,
    queryResult,
  } = useServerDynamicTableContext();
  const total = queryResult?.data?.meta?.total || 0;
  const isLoading = queryResult?.isLoading || false;

  const hasData = total > 0;
  const hasResult = table.getRowModel().rows.length > 0;

  const navigate = useNavigate();
  const { onError } = useMessages();

  const { onDuplicateContent } = useDuplicateContent({
    onSuccess: (contentId) => {
      navigate(`/disparos-de-report/editar/${contentId}/conteudo`);
    },
    onError: (error) => onError(error?.response?.data?.message),
  });

  return (
    <SkeletonText isLoaded={!isLoading}>
      {hasResult && (
        <>
          <Table mt="25px">
            <Thead>
              <Tr>
                <Th>Título</Th>
                <Th>Poder/Órgão</Th>
                <Th>PL</Th>
                <Th>Criado por</Th>
                <Th>Criado em</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                  <Td textAlign="right">
                    <ButtonGroup>
                      <IconButton
                        aria-label="Visualizar"
                        size="sm"
                        icon={<IconEye />}
                        as={Link}
                        target="_blank"
                        to={LinkContentPreview(row.original)}
                      />
                      <ButtonDuplicateContent
                        onClick={() => onDuplicateContent(row.original.id)}
                      />
                    </ButtonGroup>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          <Pagination table={table} />
        </>
      )}

      {(!hasData || !hasResult) && (
        <EmptyList
          mt="30px"
          icon={iconArrowLeftRight}
          title={
            !hasData
              ? "Você ainda não possui nenhum disparo de Report cadastrado nesse modelo de disparo"
              : "Nenhum resultado encontrado"
          }
          description={hasData ? "" : "Use o botão acima 'Criar novo conteúdo'"}
          isTable
        />
      )}
    </SkeletonText>
  );
}

const parseIsoDate = (date?: string) => {
  if (!date) {
    return null;
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const startOfDayIso = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate.toISOString();
};

const endOfDayIso = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(23, 59, 59, 999);
  return normalizedDate.toISOString();
};

const parseCsv = (value?: string) => {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const serializeCsv = (value: string[]) => value.join(",");
